import { useEffect, useState } from 'react';

import APIHandler from 'config/APIHandler';

import i18n from 'i18n-react';

/** One row from `APIHandler.getAnnouncement` (CMS New & Now notification). */
export interface CmsNotification {
  id: number | string;
  type: string;
  message: string;
  weblink: string;
}

const typePriority: Record<string, number> = {
  alert: 0,
  message: 1,
  contest: 2,
  badge: 3,
  'content-update': 4,
  events: 5,
};

function compareTypePriority(a: CmsNotification, b: CmsNotification): number {
  const aPriority = typePriority[a.type] ?? 99;
  const bPriority = typePriority[b.type] ?? 99;
  return aPriority - bPriority;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeAnnouncements(raw: unknown): CmsNotification[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: CmsNotification[] = [];
  for (const item of raw) {
    if (!isRecord(item)) {
      continue;
    }
    const id = item.id;
    const message = item.message;
    if ((typeof id !== 'number' && typeof id !== 'string') || typeof message !== 'string') {
      continue;
    }
    const type = typeof item.type === 'string' ? item.type : 'message';
    const weblink = typeof item.weblink === 'string' ? item.weblink : '';
    out.push({ id, type, message, weblink });
  }
  return out;
}

export interface UseNewAndNowNotificationsResult {
  notifications: CmsNotification[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Loads CMS New & Now notifications (`APIHandler.getAnnouncement`) for the glass panel.
 * Used by `GlassWidgetPanel` to decide whether to mount `NotificationsCard` and to pass data in.
 */
export function useNewAndNowNotifications(appId: number): UseNewAndNowNotificationsResult {
  const [notifications, setNotifications] = useState<CmsNotification[]>([]);
  /** Start true when `appId > 0` so the panel can mount the card on the first paint (avoid a flash where the card is omitted before the effect runs). */
  const [isLoading, setIsLoading] = useState(() => appId > 0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (appId <= 0) {
      setNotifications([]);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    setIsLoading(true);
    setError(null);

    const fetchNotificationData = async () => {
      try {
        const res: unknown = await APIHandler.getAnnouncement(appId);
        if (cancelled) {
          return;
        }
        const list = normalizeAnnouncements(res);
        const sorted = list.length > 1 ? [...list].sort(compareTypePriority) : list;
        setNotifications(sorted);
      } catch {
        if (!cancelled) {
          setError(i18n.translate('glassWidgets.errors.requestFailed') as string);
          setNotifications([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void fetchNotificationData();

    return () => {
      cancelled = true;
    };
  }, [appId]);

  return { notifications, isLoading, error };
}
